import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { LoaderCircle } from 'lucide-react';
import { getPasswordCriteria, PasswordCriteriaList } from 'components/ui/PasswordCriteriaList';
import { confirmSignIn, signOut } from 'aws-amplify/auth';
import { toast } from 'sonner';
import { Button } from 'components/ui/Button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { changePasswordOneTimeSchema } from 'schemas/changePasswordSchema';

type ChangePasswordFormValues = z.infer<typeof changePasswordOneTimeSchema>;

type Props = {
  onSuccess: () => void;
  onBack: () => void;
};

export const ChangePassword = ({ onSuccess, onBack }: Props) => {
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ChangePasswordFormValues>({
    resolver: zodResolver(changePasswordOneTimeSchema),
    mode: 'onChange',
    defaultValues: { newPassword: '', confirmPassword: '' },
  });

  const newPasswordValue = watch('newPassword');
  const confirmPasswordValue = watch('confirmPassword');

  const allCriteriaMet = Object.values(getPasswordCriteria(newPasswordValue)).every(Boolean);
  const passwordsMatch = newPasswordValue === confirmPasswordValue;

  const handleChangePassword = async ({ newPassword }: ChangePasswordFormValues) => {
    setLoading(true);
    try {
      await confirmSignIn({ challengeResponse: newPassword });
      await signOut();
      toast.success('Password updated! Please log in with your new password.');
      onSuccess();
    } catch (error) {
      const err = error as { message?: string };
      toast.error(err.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="space-y-5" onSubmit={handleSubmit(handleChangePassword)}>
      <p className="text-sm text-slate-500">You must set a new password before continuing.</p>

      <div className="space-y-2">
        <Label htmlFor="newPassword">New Password</Label>
        <Input
          id="newPassword"
          isPassword
          placeholder="Enter new password"
          disabled={loading}
          {...register('newPassword')}
        />
        {errors.newPassword && <p className="text-xs text-red-500">{errors.newPassword.message}</p>}
      </div>

      <PasswordCriteriaList password={newPasswordValue} />

      <div className="space-y-2">
        <Label htmlFor="confirmPassword">Confirm Password</Label>
        <Input
          id="confirmPassword"
          isPassword
          placeholder="Confirm new password"
          disabled={loading}
          {...register('confirmPassword')}
        />
        {errors.confirmPassword && (
          <p className="text-xs text-red-500">{errors.confirmPassword.message}</p>
        )}
      </div>

      <div className="space-y-3">
        <Button
          type="submit"
          className="h-12 w-full rounded-2xl text-base font-semibold"
          disabled={!allCriteriaMet || !passwordsMatch || !confirmPasswordValue || loading}
        >
          {loading ? <LoaderCircle className="h-4 w-4 animate-spin" /> : null}
          {loading ? 'Updating...' : 'Set New Password'}
        </Button>

        <Button
          type="button"
          variant="outline"
          className="h-12 w-full rounded-2xl text-base"
          disabled={loading}
          onClick={onBack}
        >
          Back
        </Button>
      </div>
    </form>
  );
};
